import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { ExternalLink, Eye, Ghost } from "lucide-react";
import { Link } from "react-router-dom";

import { DataTable } from "@/components/data/DataTable";
import { EmptyState } from "@/components/feedback/EmptyState";
import { AuditGhostRecord } from "@/components/admin/AuditGhostRecord";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { auditRouteFor } from "@/lib/auditLinks";
import { getAudit, listAudit } from "@/services/api/audit";
import type { AuditLog } from "@/services/api/types";

/** The audit trail: every non-read action with field-level diffs (Phase 2). */
export function AuditTrailPage() {
  const { t } = useTranslation();
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  // Entities without a record page (bay_schedule, import rows, …) have no
  // deep-link target; their `record` cell opens an in-place ghost snapshot.
  const [ghost, setGhost] = useState<{ entity_type: string; entity_id: string | null } | null>(
    null,
  );
  const limit = 25;
  const query = useQuery({
    queryKey: ["audit", { limit, offset }],
    queryFn: () => listAudit({ limit, offset }),
  });
  const detail = useQuery({
    queryKey: ["audit", selected],
    queryFn: () => (selected ? getAudit(selected) : Promise.resolve(null)),
    enabled: selected !== null,
  });

  const columns: ColumnDef<AuditLog>[] = [
    {
      accessorKey: "occurred_at",
      header: t("admin.changedAt"),
      cell: ({ getValue }) => new Date(String(getValue())).toLocaleString(),
    },
    {
      accessorKey: "actor_email",
      header: t("admin.actor"),
      cell: ({ getValue, row }) =>
        getValue() || (row.original.actor_user_id ? `#${row.original.actor_user_id}` : "—"),
    },
    { accessorKey: "action", header: t("admin.action") },
    {
      accessorKey: "entity_type",
      header: t("admin.entity"),
      cell: ({ getValue }) => String(getValue()),
    },
    {
      accessorKey: "entity_id",
      header: t("admin.record"),
      cell: ({ getValue, row }) => {
        // Same deep-link contract as the admin summary: the target page
        // highlights what changed (or shows a ghost copy when deleted).
        const route = auditRouteFor(row.original, row.original.id);
        if (route) {
          return (
            <Link to={route} className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
              {String(getValue() ?? "—")}
              <ExternalLink className="h-3 w-3" />
            </Link>
          );
        }
        // No record page: offer the rebuilt snapshot right here instead of a
        // dead end — every audit row stays auditable from the trail.
        return (
          <button
            type="button"
            onClick={() =>
              setGhost({
                entity_type: row.original.entity_type,
                entity_id: row.original.entity_id,
              })
            }
            title={t("admin.auditSnapshot")}
            className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
          >
            {String(getValue() ?? "—")}
            <Ghost className="h-3 w-3" aria-hidden />
          </button>
        );
      },
    },
    {
      id: "detail",
      header: "",
      cell: ({ row }) => (
        <Button variant="ghost" size="sm" onClick={() => setSelected(row.original.id)}>
          <Eye className="h-4 w-4" /> {t("common.view")}
        </Button>
      ),
    },
  ];

  const changes = detail.data?.changes ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">{t("nav.audit")}</h1>
      </div>

      {!query.isLoading && (query.data?.total ?? 0) === 0 ? (
        <EmptyState title={t("common.emptyState")} />
      ) : (
        <DataTable
          columns={columns}
          data={query.data?.items ?? []}
          total={query.data?.total ?? 0}
          limit={limit}
          offset={offset}
          onPageChange={setOffset}
          isLoading={query.isLoading}
          onRowDoubleClick={(row) => setSelected(row.id)}
        />
      )}

      <Dialog open={selected !== null} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {detail.data?.action} · {detail.data?.entity_type}
              {detail.data?.entity_id ? ` #${detail.data.entity_id}` : ""}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
              <div>
                {t("admin.actor")}:{" "}
                <span className="text-foreground">
                  {detail.data?.actor_email ?? detail.data?.actor_user_id ?? "—"}
                </span>
              </div>
              <div>
                {t("admin.changedAt")}:{" "}
                <span className="text-foreground">
                  {detail.data ? new Date(detail.data.occurred_at).toLocaleString() : "—"}
                </span>
              </div>
              <div>
                {t("admin.entity")}: <span className="text-foreground">{detail.data?.entity_type}</span>
              </div>
              <div>
                {t("admin.record")}:{" "}
                <span className="text-foreground">{detail.data?.entity_id ?? "—"}</span>
              </div>
              <div>
                IP: <span className="text-foreground">{detail.data?.ip_address ?? "—"}</span>
              </div>
              <div>
                Correlation:{" "}
                <span className="font-mono text-foreground">{detail.data?.correlation_id ?? "—"}</span>
              </div>
            </div>

            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("admin.field")}</TableHead>
                    <TableHead>{t("admin.from")}</TableHead>
                    <TableHead>{t("admin.to")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {changes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="py-4 text-center text-muted-foreground">
                        {detail.data?.action === "insert"
                          ? `${detail.data?.actor_email ?? t("admin.actor")} ${t("admin.inserted")} ${detail.data?.entity_type}`
                          : "—"}
                      </TableCell>
                    </TableRow>
                  ) : (
                    changes.map((change, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-mono text-xs">{change.field_name}</TableCell>
                        <TableCell className="max-w-[12rem] truncate whitespace-pre-wrap text-xs">
                          {change.old_value ?? "—"}
                        </TableCell>
                        <TableCell className="max-w-[12rem] whitespace-pre-wrap text-xs">
                          {change.new_value ?? "—"}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Fallback snapshot for entities without a record page. */}
      <Dialog open={ghost !== null} onOpenChange={(open) => !open && setGhost(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("admin.auditSnapshot")}</DialogTitle>
            <DialogDescription>{t("admin.auditSnapshotHint")}</DialogDescription>
          </DialogHeader>
          {ghost ? (
            <AuditGhostRecord
              entityType={ghost.entity_type}
              entityId={ghost.entity_id}
              variant="snapshot"
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
